import React, { useState, useEffect } from 'react';

/**
 * ChatDiagnosticPanel — READ-ONLY runtime instrumentation.
 *
 * Renders a small collapsible debug panel at the top of the chat modal.
 * Used to diagnose why the EventSummaryCard is missing on iOS TestFlight
 * and why the chat snaps back to the bottom.
 *
 * Activation (any of the following):
 *   - localStorage.setItem('celey_chat_debug', '1')
 *   - URL contains ?chatdebug=1   (auto-persists to localStorage)
 *   - window.__CELEY_CHAT_DEBUG = true
 *   - NODE_ENV !== 'production'   (so preview env always shows it)
 *   - 5-tap activation on the panel's tiny anchor (see useTapActivation)
 *
 * Does NOT change any chat behavior — render-only, no side effects on
 * conversation state, no extra API calls, no navigation hooks.
 *
 * Branch: emergent/chat-runtime-diagnostics-0601
 */

export const isDiagEnabled = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (process.env.NODE_ENV !== 'production') return true;
    if (window.__CELEY_CHAT_DEBUG === true) return true;
    const ls = window.localStorage?.getItem('celey_chat_debug');
    if (ls === '1' || ls === 'true') return true;
    if (typeof window.location?.search === 'string' && window.location.search.indexOf('chatdebug=1') !== -1) {
      try { window.localStorage?.setItem('celey_chat_debug', '1'); } catch (_) { /* noop */ }
      return true;
    }
  } catch (_) { /* noop */ }
  return false;
};

const Row = ({ k, v, ok }) => {
  let val = v;
  if (v === true) val = 'true';
  else if (v === false) val = 'false';
  else if (v === null || v === undefined) val = '∅';
  else if (typeof v === 'object') {
    try { val = JSON.stringify(v); } catch (_) { val = String(v); }
  }
  const color = ok === true ? '#16a34a' : ok === false ? '#dc2626' : '#0f172a';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 10, lineHeight: 1.35, padding: '1px 0' }}>
      <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{k}</span>
      <span style={{ color, textAlign: 'right', wordBreak: 'break-all', maxWidth: '70%' }}>{String(val)}</span>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginTop: 6 }}>
    <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6, color: '#0ea5e9', fontWeight: 700, marginBottom: 2 }}>{title}</div>
    {children}
  </div>
);

const ChatDiagnosticPanel = ({
  mountedComponent,
  switcherFlagRaw,
  switcherFlagBranch,
  userRoleProp,
  inquiryId,
  inquiryStatusProp,
  selectedInquiry,
  convData,
  fetchUrl,
  lastFetchStatus,
  lastFetchError,
  loading,
  isOpen,
  isCompactMode,
  isLandscape,
  keyboardInset,
  showCompactBar,
  messages,
  prevMessageCount,
  userScrolledAway,
  lastAutoScrollReason,
  scrollMetrics,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [tick, setTick] = useState(0);
  const [tapState, setTapState] = useState({ count: 0, ts: 0 });
  const enabled = isDiagEnabled();

  // Re-render once per second so live values (scrollTop, keyboardInset) stay current
  useEffect(() => {
    if (!enabled || !expanded) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [enabled, expanded]);

  // 5-tap activation fallback — taps on the tiny anchor flip the localStorage flag.
  const handleAnchorTap = () => {
    const now = Date.now();
    const within = now - tapState.ts < 2000;
    const next = within ? tapState.count + 1 : 1;
    setTapState({ count: next, ts: now });
    if (next >= 5) {
      try {
        const current = window.localStorage?.getItem('celey_chat_debug') === '1';
        window.localStorage?.setItem('celey_chat_debug', current ? '0' : '1');
        // eslint-disable-next-line no-alert
        window.alert(`Chat debug ${current ? 'DISABLED' : 'ENABLED'}. Reopen chat to apply.`);
      } catch (_) { /* noop */ }
      setTapState({ count: 0, ts: 0 });
    }
  };

  // Always render the tiny tap-anchor so the user can enable diag even when off.
  if (!enabled) {
    return (
      <div
        data-testid="chat-diag-anchor"
        onClick={handleAnchorTap}
        style={{
          position: 'absolute', top: 2, right: 38, width: 18, height: 18, zIndex: 70,
          opacity: 0.001, cursor: 'pointer',
        }}
        aria-hidden="true"
      />
    );
  }

  // ── Compute derived values for display ──
  const eventSummary = convData?.event_summary;
  const inquiry = convData?.inquiry;
  const booking = convData?.booking;

  const gateLeft = !isCompactMode;
  const gateRight = !!(eventSummary || inquiry || booking);
  const gatePass = gateLeft && gateRight;
  const gateReason = (() => {
    if (gatePass) return 'pass';
    const reasons = [];
    if (!gateLeft) reasons.push(`isCompactMode=true (landscape=${isLandscape}, kbd=${keyboardInset})`);
    if (!gateRight) reasons.push('all three (eventSummary/inquiry/booking) are null');
    return reasons.join(' & ');
  })();

  // Auth/role snapshot (from localStorage so we don't import context here)
  let activeRole = null;
  let tokenPresent = false;
  let userEmail = null;
  try {
    activeRole = window.localStorage?.getItem('activeRole');
    tokenPresent = !!window.localStorage?.getItem('token');
    // user object is sometimes mirrored to localStorage as 'user'
    const u = window.localStorage?.getItem('user');
    if (u) {
      try { userEmail = JSON.parse(u)?.email || null; } catch (_) { /* noop */ }
    }
  } catch (_) { /* noop */ }

  // Role-switch confirm dialog detection (DOM probe — read-only)
  let roleSwitchModalOpen = false;
  try {
    roleSwitchModalOpen = !!document.querySelector('[data-testid="role-switch-confirm-dialog"]');
  } catch (_) { /* noop */ }

  // Pathname when this chat opened
  let pathname = null;
  try { pathname = window.location?.pathname; } catch (_) { /* noop */ }

  // Live scroll metrics
  const sm = (typeof scrollMetrics === 'function') ? scrollMetrics() : (scrollMetrics || {});

  const messageCount = Array.isArray(messages) ? messages.length : 0;

  return (
    <div
      data-testid="chat-diag-panel"
      style={{
        margin: '4px 6px 0',
        padding: 6,
        borderRadius: 6,
        background: 'rgba(15, 23, 42, 0.92)',
        color: '#e2e8f0',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 10,
        lineHeight: 1.35,
        zIndex: 65,
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}
    >
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 8 }}
        data-testid="chat-diag-toggle"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: 4,
            background: mountedComponent === 'ConversationThread' ? '#22c55e' : '#f97316',
          }} />
          <strong style={{ color: '#f1f5f9' }}>CHAT DIAG</strong>
          <span style={{ color: '#cbd5e1' }}>{mountedComponent}</span>
          <span style={{ color: gatePass ? '#22c55e' : '#f87171' }}>
            {gatePass ? '· header-gate=PASS' : '· header-gate=FAIL'}
          </span>
        </span>
        <span style={{ color: '#94a3b8' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 6, color: '#e2e8f0' }} data-key={tick /* force update */}>
          <Section title="Component Identity">
            <Row k="mountedComponent" v={mountedComponent} ok={mountedComponent === 'ConversationThread'} />
            <Row k="switcher.flagRaw" v={switcherFlagRaw} />
            <Row k="switcher.branch" v={switcherFlagBranch} ok={switcherFlagBranch === 'ConversationThread'} />
            <Row k="NODE_ENV" v={process.env.NODE_ENV} />
            <Row k="bundle.flag" v={process.env.REACT_APP_ENABLE_CONVERSATION_FIRST} />
            <Row k="pathname" v={pathname} />
          </Section>

          <Section title="Role / Navigation State">
            <Row k="user.email" v={userEmail} />
            <Row k="activeRole(LS)" v={activeRole} />
            <Row k="userRole prop" v={userRoleProp} ok={!!userRoleProp} />
            <Row k="token present" v={tokenPresent} ok={tokenPresent} />
            <Row k="roleSwitchModal open" v={roleSwitchModalOpen} />
            <Row k="selectedInquiry.id" v={selectedInquiry?.id} />
            <Row k="selectedInquiry.status" v={selectedInquiry?.status} />
            <Row k="selectedInquiry.customer_id" v={selectedInquiry?.customer_id} />
            <Row k="selectedInquiry.vendor_id" v={selectedInquiry?.vendor_id} />
            <Row k="opened before role complete?" v={activeRole && userRoleProp && activeRole !== userRoleProp} ok={!(activeRole && userRoleProp && activeRole !== userRoleProp)} />
          </Section>

          <Section title="Conversation Data">
            <Row k="inquiryId prop" v={inquiryId} ok={!!inquiryId} />
            <Row k="fetch URL" v={fetchUrl} />
            <Row k="last fetch status" v={lastFetchStatus} />
            <Row k="last fetch error" v={lastFetchError} ok={!lastFetchError} />
            <Row k="loading" v={loading} />
            <Row k="convData loaded" v={!!convData} ok={!!convData} />
            <Row k="event_summary present" v={!!eventSummary} ok={!!eventSummary} />
            <Row k="inquiry present" v={!!inquiry} ok={!!inquiry} />
            <Row k="booking present" v={!!booking} ok={!!booking} />
            <Row k="booking.status" v={booking?.status} />
            <Row k="inquiry.status" v={inquiry?.status} />
            <Row k="inquiry.event_type" v={inquiry?.event_type} />
            <Row k="inquiry.event_date" v={inquiry?.event_date} />
            <Row k="event_summary.event_type" v={eventSummary?.event_type} />
            <Row k="event_summary.event_date" v={eventSummary?.event_date} />
          </Section>

          <Section title="Header Render Gate">
            <Row k="isOpen" v={isOpen} />
            <Row k="isCompactMode" v={isCompactMode} ok={!isCompactMode} />
            <Row k="isLandscape" v={isLandscape} />
            <Row k="keyboardInset" v={keyboardInset} />
            <Row k="showCompactBar" v={showCompactBar} />
            <Row k="!isCompactMode" v={gateLeft} ok={gateLeft} />
            <Row k="(eventSummary||inquiry||booking)" v={gateRight} ok={gateRight} />
            <Row k="HEADER GATE RESULT" v={gatePass ? 'PASS — header should render' : 'FAIL'} ok={gatePass} />
            <Row k="if FAIL reason" v={gateReason} ok={gatePass} />
          </Section>

          <Section title="Scroll Behavior">
            <Row k="message count" v={messageCount} />
            <Row k="prev message count" v={prevMessageCount} />
            <Row k="userScrolledAway" v={userScrolledAway} />
            <Row k="lastAutoScrollReason" v={lastAutoScrollReason} />
            <Row k="scrollTop" v={sm.scrollTop} />
            <Row k="scrollHeight" v={sm.scrollHeight} />
            <Row k="clientHeight" v={sm.clientHeight} />
            <Row k="distFromBottom" v={(typeof sm.scrollHeight === 'number' && typeof sm.scrollTop === 'number' && typeof sm.clientHeight === 'number') ? (sm.scrollHeight - sm.scrollTop - sm.clientHeight) : null} />
          </Section>

          <div style={{ marginTop: 6, fontSize: 9, color: '#64748b' }}>
            Branch: emergent/chat-runtime-diagnostics-0601 · read-only · tick {tick}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDiagnosticPanel;
